/**
 * Leaving, and what has to be forgotten on the way out.
 *
 * ── Why this is not one line in a menu item ──────────────────────────────
 *
 * Signing out is two facts becoming false at once: the server's session, and
 * everything this browser has cached *because* of it. Getting only the first
 * leaves a signed-out page rendering the last person's students; getting only
 * the second leaves a signed-in cookie behind a screen that says sign in.
 *
 * So the order matters and it is the whole of this file:
 *
 * 1. Ask the API to end the session. A POST, because Better Auth's `/sign-out`
 *    refuses a GET — the previous version of this code assigned the endpoint
 *    to `window.location.href`, which navigated, was rejected, and left the
 *    cookie in place.
 * 2. Clear Apollo. `clearStore` rather than `resetStore`: the latter refetches
 *    every active query, which for a person who has just signed out means a
 *    burst of requests that are all going to 401.
 * 3. Leave by assigning `location`, not by routing. A full document load is
 *    what discards the router's loader cache, every component's state, and any
 *    response still in flight — the last of which is the one that could
 *    otherwise land after the cache was cleared and repopulate it with the
 *    previous person's data.
 *
 * There is deliberately no Jotai state to clear. The package is a dependency
 * of this app and nothing in it holds an atom today; server state lives in
 * Apollo, which step 2 covers. If an atom is ever added for genuine client
 * state, its reset belongs here, next to the store it is cleared beside.
 */
import type { ApolloClient } from '@apollo/client'
import type { Language } from '@athena/i18n'

import { AUTH_ENDPOINTS } from './apollo.js'

export interface SignOutDeps {
  /** Injected in tests. Defaults to a full document load. */
  navigate?: (href: string) => void
  /** Injected in tests. Defaults to the runtime's own fetch. */
  fetch?: typeof globalThis.fetch
}

/**
 * Ends the session and lands the person on the sign-in screen, in the language
 * they were reading.
 *
 * The request is awaited but its outcome is not branched on. A sign-out that
 * the network lost still has to clear this browser: refusing to forget because
 * the server did not answer would leave a shared machine showing one person's
 * work to the next. The server-side session is the one that matters for
 * access, and it expires on its own.
 */
export async function signOut(
  client: Pick<ApolloClient, 'clearStore'>,
  language: Language,
  deps: SignOutDeps = {},
): Promise<void> {
  const request = deps.fetch ?? globalThis.fetch
  const go = deps.navigate ?? ((href: string) => window.location.assign(href))

  try {
    await request(AUTH_ENDPOINTS.signOut, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    })
  } catch {
    // Offline, or the API is down. Clear this browser anyway — see above.
  }

  await client.clearStore()

  go(`/${language}/sign-in`)
}
